import React, { useState } from "react";
import { Card, Upload, Select, Input, Switch, message } from "antd";
import { jsPDF } from "jspdf";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  FilePdfOutlined,
  FileImageOutlined,
} from "@ant-design/icons";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB per image
const MAX_LONG_EDGE = 4000;

const PAGE_SIZES_MM = {
  a4: { w: 210, h: 297 },
  letter: { w: 215.9, h: 279.4 },
};

const MARGINS_MM = {
  none: 0,
  small: 10,
  medium: 20,
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i ? 2 : 0)} ${sizes[i]}`;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode_failed"));
    img.src = src;
  });

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(file);
  });

const downscaleIfNeeded = (img) => {
  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  if (longEdge <= MAX_LONG_EDGE) {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.92),
      width: canvas.width,
      height: canvas.height,
    };
  }
  const scale = MAX_LONG_EDGE / longEdge;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.9), width: w, height: h };
};

const ImagesToPdf = () => {
  const [items, setItems] = useState([]);
  const [pageSize, setPageSize] = useState("a4");
  const [orientation, setOrientation] = useState("auto");
  const [margin, setMargin] = useState("small");
  const [onePerPage, setOnePerPage] = useState(true);
  const [filename, setFilename] = useState("images.pdf");
  const [generating, setGenerating] = useState(false);
  const { t } = useTranslation();

  const beforeUpload = async (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      message.error(t("images_to_pdf_unsupported_type"));
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(t("images_to_pdf_too_large"));
      return Upload.LIST_IGNORE;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      const img = await loadImage(dataUrl);
      setItems((prev) => [
        ...prev,
        {
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          file,
          name: file.name,
          size: file.size,
          width: img.naturalWidth,
          height: img.naturalHeight,
          preview: dataUrl,
        },
      ]);
    } catch (err) {
      message.error(`${t("failed_to_process")} ${file.name}`);
    }
    return false;
  };

  const handleReorder = (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    const next = [...items];
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setItems(next);
  };

  const handleDelete = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleClearAll = () => setItems([]);

  const handleGenerate = async () => {
    if (items.length === 0) {
      message.error(t("images_to_pdf_add_images_first"));
      return;
    }

    setGenerating(true);
    message.loading({
      content: t("images_to_pdf_generating"),
      key: "img2pdf",
      duration: 0,
    });

    try {
      const marginMm = MARGINS_MM[margin] ?? MARGINS_MM.small;
      let pdf = null;

      const newPdfWithImage = (imgWidth, imgHeight) => {
        if (pageSize === "fit") {
          // Page exactly matches image px → use jsPDF's "px" units
          return new jsPDF({
            orientation: imgWidth >= imgHeight ? "landscape" : "portrait",
            unit: "px",
            format: [imgWidth, imgHeight],
            hotfixes: ["px_scaling"],
          });
        }
        const base = PAGE_SIZES_MM[pageSize] || PAGE_SIZES_MM.a4;
        let pageW = base.w;
        let pageH = base.h;
        let pdfOrientation = orientation;
        if (pdfOrientation === "auto") {
          pdfOrientation = imgWidth >= imgHeight ? "landscape" : "portrait";
        }
        if (pdfOrientation === "landscape") {
          [pageW, pageH] = [Math.max(pageW, pageH), Math.min(pageW, pageH)];
        } else {
          [pageW, pageH] = [Math.min(pageW, pageH), Math.max(pageW, pageH)];
        }
        return new jsPDF({
          orientation: pdfOrientation,
          unit: "mm",
          format: [pageW, pageH],
        });
      };

      // Pre-process all images (decode + downscale) so we don't block UI mid-loop
      const prepared = [];
      for (const item of items) {
        try {
          const img = await loadImage(item.preview);
          prepared.push({ ...item, processed: downscaleIfNeeded(img) });
        } catch (err) {
          throw new Error(`${t("failed_to_process")} ${item.name}`);
        }
      }

      if (onePerPage || pageSize === "fit") {
        for (let i = 0; i < prepared.length; i += 1) {
          const { processed } = prepared[i];
          const { dataUrl, width, height } = processed;

          if (i === 0) {
            pdf = newPdfWithImage(width, height);
          } else if (pageSize === "fit") {
            pdf.addPage([width, height], width >= height ? "l" : "p");
          } else {
            // Use orientation matching this specific image when "auto"
            const o =
              orientation === "auto"
                ? width >= height
                  ? "landscape"
                  : "portrait"
                : orientation;
            const base = PAGE_SIZES_MM[pageSize] || PAGE_SIZES_MM.a4;
            let pw = base.w;
            let ph = base.h;
            if (o === "landscape") {
              [pw, ph] = [Math.max(pw, ph), Math.min(pw, ph)];
            } else {
              [pw, ph] = [Math.min(pw, ph), Math.max(pw, ph)];
            }
            pdf.addPage([pw, ph], o === "landscape" ? "l" : "p");
          }

          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();

          if (pageSize === "fit") {
            pdf.addImage(dataUrl, "JPEG", 0, 0, pageW, pageH);
          } else {
            const usableW = pageW - 2 * marginMm;
            const usableH = pageH - 2 * marginMm;
            const ratio = Math.min(usableW / width, usableH / height);
            const drawW = width * ratio;
            const drawH = height * ratio;
            const x = (pageW - drawW) / 2;
            const y = (pageH - drawH) / 2;
            pdf.addImage(dataUrl, "JPEG", x, y, drawW, drawH);
          }
        }
      } else {
        // Pack mode: many images per page, top-to-bottom, with margins
        const base = PAGE_SIZES_MM[pageSize] || PAGE_SIZES_MM.a4;
        let pdfOrientation = orientation === "auto" ? "portrait" : orientation;
        let pw = base.w;
        let ph = base.h;
        if (pdfOrientation === "landscape") {
          [pw, ph] = [Math.max(pw, ph), Math.min(pw, ph)];
        } else {
          [pw, ph] = [Math.min(pw, ph), Math.max(pw, ph)];
        }
        pdf = new jsPDF({
          orientation: pdfOrientation,
          unit: "mm",
          format: [pw, ph],
        });

        const usableW = pw - 2 * marginMm;
        const usableH = ph - 2 * marginMm;
        const gap = 4; // mm between packed images
        let cursorY = marginMm;

        for (let i = 0; i < prepared.length; i += 1) {
          const { processed } = prepared[i];
          const { dataUrl, width, height } = processed;
          const ratio = Math.min(usableW / width, usableH / height);
          const drawW = width * ratio;
          const drawH = height * ratio;

          if (cursorY + drawH > marginMm + usableH && cursorY !== marginMm) {
            pdf.addPage([pw, ph], pdfOrientation === "landscape" ? "l" : "p");
            cursorY = marginMm;
          }
          const x = marginMm + (usableW - drawW) / 2;
          pdf.addImage(dataUrl, "JPEG", x, cursorY, drawW, drawH);
          cursorY += drawH + gap;
        }
      }

      let outName = (filename || "images.pdf").trim();
      if (!outName.toLowerCase().endsWith(".pdf")) outName += ".pdf";
      pdf.save(outName);

      message.success({
        content: t("images_to_pdf_generated"),
        key: "img2pdf",
      });
    } catch (err) {
      message.error({
        content: err?.message || t("images_to_pdf_failed"),
        key: "img2pdf",
      });
    } finally {
      setGenerating(false);
    }
  };

  const totalSize = items.reduce((acc, i) => acc + i.size, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Card className="rounded-xl shadow-lg border-0 overflow-hidden bg-white">
          {items.length === 0 && (
            <div className="min-h-[400px] flex flex-col items-center justify-center">
              <div className="text-center p-8">
                <div className="p-4 bg-blue-100 rounded-full inline-block mb-6">
                  <FileImageOutlined className="text-4xl text-blue-600" />
                </div>

                <Upload
                  beforeUpload={beforeUpload}
                  showUploadList={false}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                >
                  <StandardButton
                    type="primary"
                    size="large"
                    className="px-8 py-6 h-auto"
                    icon={<PlusOutlined />}
                  >
                    {t("images_to_pdf_select_images")}
                  </StandardButton>
                </Upload>

                <div className="mt-4 text-sm text-gray-400">
                  {t("images_to_pdf_supported")}
                </div>
              </div>
            </div>
          )}

          {items.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                <div className="text-gray-700">
                  {t("images_to_pdf_selected")}:{" "}
                  <span className="font-semibold">{items.length}</span>
                  <span className="text-gray-400 ml-2">
                    ({formatBytes(totalSize)})
                  </span>
                </div>
                <div className="flex gap-2">
                  <Upload
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                  >
                    <StandardButton
                      type="secondary"
                      size="middle"
                      icon={<PlusOutlined />}
                    >
                      {t("add_more")}
                    </StandardButton>
                  </Upload>
                  <StandardButton
                    type="secondary"
                    danger
                    size="middle"
                    onClick={handleClearAll}
                  >
                    {t("clear_all")}
                  </StandardButton>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {items.map((p, i) => (
                  <div
                    key={p.id}
                    className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <img
                          src={p.preview}
                          alt={p.name}
                          className="w-20 h-24 object-cover rounded border border-gray-200"
                        />
                        <div className="absolute -top-2 -left-2 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          <h4
                            className="font-medium text-gray-800 truncate text-sm"
                            title={p.name}
                          >
                            {p.name}
                          </h4>
                          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                            <span>
                              {p.width}×{p.height}px
                            </span>
                            <span>• {formatBytes(p.size)}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            onClick={() => handleReorder(i, -1)}
                            disabled={i === 0}
                            icon={<ArrowUpOutlined />}
                          >
                            {t("up")}
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            onClick={() => handleReorder(i, 1)}
                            disabled={i === items.length - 1}
                            icon={<ArrowDownOutlined />}
                          >
                            {t("down")}
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            danger
                            onClick={() => handleDelete(i)}
                            icon={<DeleteOutlined />}
                          >
                            {t("remove")}
                          </StandardButton>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("images_to_pdf_page_size")}
                  </label>
                  <Select
                    value={pageSize}
                    onChange={setPageSize}
                    className="w-full"
                    options={[
                      { value: "a4", label: "A4" },
                      { value: "letter", label: "Letter" },
                      {
                        value: "fit",
                        label: t("images_to_pdf_fit_to_image"),
                      },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("images_to_pdf_orientation")}
                  </label>
                  <Select
                    value={orientation}
                    onChange={setOrientation}
                    disabled={pageSize === "fit"}
                    className="w-full"
                    options={[
                      { value: "auto", label: t("images_to_pdf_auto") },
                      {
                        value: "portrait",
                        label: t("images_to_pdf_portrait"),
                      },
                      {
                        value: "landscape",
                        label: t("images_to_pdf_landscape"),
                      },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("images_to_pdf_margin")}
                  </label>
                  <Select
                    value={margin}
                    onChange={setMargin}
                    disabled={pageSize === "fit"}
                    className="w-full"
                    options={[
                      { value: "none", label: t("images_to_pdf_margin_none") },
                      {
                        value: "small",
                        label: t("images_to_pdf_margin_small"),
                      },
                      {
                        value: "medium",
                        label: t("images_to_pdf_margin_medium"),
                      },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("images_to_pdf_filename")}
                  </label>
                  <Input
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    placeholder="images.pdf"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <Switch
                  checked={onePerPage}
                  onChange={setOnePerPage}
                  disabled={pageSize === "fit"}
                />
                <span className="text-sm text-gray-700">
                  {t("images_to_pdf_one_per_page")}
                </span>
              </div>

              <div className="text-center pt-4 border-t border-gray-100">
                <StandardButton
                  onClick={handleGenerate}
                  type="primary"
                  size="large"
                  loading={generating}
                  className="px-8 py-4"
                  icon={<FilePdfOutlined />}
                >
                  {t("images_to_pdf_generate")}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default ImagesToPdf;
